import moment from 'moment';
import TimerSandIcon from 'mdi-react/TimerSandIcon';
import CalendarClockIcon from 'mdi-react/CalendarClockIcon';
import AlarmIcon from 'mdi-react/AlarmIcon';
import HeartOutlineIcon from 'mdi-react/HeartOutlineIcon';
import { partial } from '../../util/translation/translation';
import Api from '../../util/api';

// Ready-made messages for an event, timed from its dates (used on create
// and on the Communication tab). Fixed times are local: 18:00 the day
// before, 10:00 the day after.
export const EVENT_MESSAGE_TEMPLATES = [
  'registrationEnds',
  'tomorrow',
  'startsSoon',
  'thanks',
];

/**
 * The templates for an event ({ title, startDate, endDate, registrationEnd }):
 * when each is sent, to whom, and whether it can still be used (`noDate`:
 * the event has no date for it; `tooLate`: its time has passed).
 */
export const getEventMessageTemplates = (event) => {
  const name = event?.title || '';
  const start = event?.startDate ? moment(event.startDate) : null;
  const end = event?.endDate ? moment(event.endDate) : start;
  const registrationEnd = event?.registrationEnd
    ? moment(event.registrationEnd)
    : null;
  const list = [
    {
      key: 'registrationEnds',
      icon: TimerSandIcon,
      sendAt: registrationEnd?.clone().subtract(3, 'hours'),
      receiverType: 'PRIVILAGED',
      noDate: !registrationEnd,
      values: { event: name, time: registrationEnd?.format('LT') },
    },
    {
      key: 'tomorrow',
      icon: CalendarClockIcon,
      sendAt: start
        ?.clone()
        .subtract(1, 'day')
        .set({ hour: 18, minute: 0, second: 0 }),
      receiverType: 'PARTICIPANTS',
      noDate: !start,
      values: { event: name, time: start?.format('LT') },
    },
    {
      key: 'startsSoon',
      icon: AlarmIcon,
      sendAt: start?.clone().subtract(1, 'hour'),
      receiverType: 'PARTICIPANTS',
      noDate: !start,
      values: { event: name, time: start?.format('LT') },
    },
    {
      key: 'thanks',
      icon: HeartOutlineIcon,
      sendAt: end
        ?.clone()
        .add(1, 'day')
        .set({ hour: 10, minute: 0, second: 0 }),
      receiverType: 'PARTICIPANTS',
      noDate: !end,
      values: { event: name },
    },
  ];
  return list.map((template) => ({
    ...template,
    tooLate:
      !template.noDate &&
      (!template.sendAt.isValid() || !template.sendAt.isAfter(moment())),
  }));
};

/** A template's title and text, in the admin's language. */
export const templateMessage = (template) => {
  const p = partial('EventsContactForm');
  return {
    title: p(`template-${template.key}-title`, template.values),
    body: p(`template-${template.key}-body`, template.values),
  };
};

/** "Sends Tue 14 Oct 15:00", or why it can't be used. */
export const templateWhen = (template) => {
  const p = partial('EventsContactForm');
  if (template.noDate) return p('templateNoDate');
  if (template.tooLate) return p('templateTooLate');
  return p('templateSendsAt', {
    when: template.sendAt.format('ddd D MMM LT'),
  });
};

// The event's dates moved `weeks` weeks on (as the backend makes its weekly
// copies: same local time, 7 calendar days per week)
const shiftWeeks = (values, weeks) => {
  if (!weeks) return values;
  const shift = (date) =>
    date
      ? moment(date)
          .add(weeks * 7, 'days')
          .toISOString()
      : date;
  return {
    ...values,
    startDate: shift(values.startDate),
    endDate: shift(values.endDate),
    registrationEnd: shift(values.registrationEnd),
  };
};

/**
 * Schedules the ready-made messages ticked on the event form
 * (values.scheduleMessages) as push messages: for the new event and, for a
 * recurring one, each weekly copy (`copyIds`, week 1 first), each timed from
 * its own dates. Ones whose time has passed are skipped. Returns how many
 * couldn't be scheduled.
 */
export const scheduleEventTemplateMessages = async ({
  eventId,
  copyIds = [],
  values,
  clubName,
}) => {
  const requests = [eventId, ...copyIds].flatMap((id, weeks) =>
    getEventMessageTemplates(shiftWeeks(values, weeks))
      .filter(
        (template) =>
          values.scheduleMessages?.[template.key] &&
          !template.noDate &&
          !template.tooLate
      )
      .map((template) => {
        const message = templateMessage(template);
        return Api.events.scheduleMessage(id, {
          messageTitle: message.title,
          messageBody: message.body,
          type: 'push',
          receiverType: template.receiverType,
          scheduledDate: moment.utc(template.sendAt.toDate()).format(),
          clubName,
        });
      })
  );
  const results = await Promise.allSettled(requests);
  return results.filter((result) => result.status === 'rejected').length;
};
